/**
 * INVISIBILITY — thirty seconds of being a ghost: nothing in the world can reach you, and you can
 * still eat.
 *
 * The player report, verbatim: "Another power up should be invisibility for 30 seconds. It allows
 * you to pass through all circles and spike balls along with rockets not damaging you, grappling
 * hook going through you, no hit on frost, etc... The only thing it does allow is eating dots and
 * still swallowing circles smaller than you."
 *
 * **IT IS THE SECOND ABILITY THAT PUTS NOTHING IN THE WORLD**, so it carries a `SelfSpec` where
 * five of the seven carry a `ProjectileSpec` — the teleport's arrangement, which is the whole
 * reason `ProjectileSpec` became optional. What it leaves behind is a *deadline on the player*
 * (`PlayerState.invisibleUntilTick`), which is the freeze's and the frost's arrangement one door
 * along: computed, never swept, hashed because it decides what may happen to somebody.
 *
 * **IT IS NOT A RENDERING TRICK, AND THAT IS THE WHOLE DESIGN.** The word is taken literally on
 * both sides at once:
 *
 * - To the *simulation* it is immunity. A ghost is not a valid target for any projectile, is not
 *   bitten by a spike, cannot be eaten and cannot be pulled into a duel — and it does not even
 *   bounce off an evenly matched stranger, because "pass through all circles" is the report's
 *   own first clause. What it keeps is the two things the report kept: pellets, and swallowing
 *   anybody small enough to swallow. Every one of those is spelled out in the phase it belongs
 *   to (`RoomSim`), never bolted onto `duelImmuneUntil` — that window is a *grace*, it runs both
 *   ways and it is half a second long, and folding a thirty-second one-way immunity into it
 *   would make two very different things one field.
 * - To *replication* it is absence. A ghost is taken out of every other viewer's interest set,
 *   so no other client is sent their position at all — not on the arena, not on the minimap,
 *   not to a spectator, and not to a modified client either. See `selectInterest` for why that
 *   is the only honest reading of the word, and for what it costs.
 *
 * **THE WINDOW AND THE VANISH ARE TWO DIFFERENT THIRTY SECONDS.** The use model is `timed`, so
 * the *slot* burns from the tick the orb is taken whether or not the button is ever pressed; the
 * *status* is thirty seconds from the press. So one orb is "thirty seconds to decide, and thirty
 * seconds of being gone", and a player who sits on it loses the chance rather than the ability.
 * That is the timed model's own stated intent — a window is burning whether you spend it or not
 * — applied to an ability whose value is entirely in *when* you spend it.
 *
 * **A SECOND PRESS WHILE IT IS RUNNING DOES NOTHING AT ALL**, which is what bounds the whole
 * thing at exactly thirty seconds of invisibility per orb. Without that rule the timed model
 * would let somebody re-press every `cooldownTicks` through their window and walk out of one orb
 * with nearly sixty seconds of immunity — the cooldown cannot prevent it, because a per-slot
 * cooldown short enough to be a rate limit is far shorter than the window it sits inside. The
 * rule lives in `RoomSim` beside the effect it is about; the press is spent and costs nothing.
 *
 * **THE ROOM IS TOLD THAT SOMEBODY VANISHED AND NEVER WHERE.** An ability with no projectile
 * raises a `powerup_used` notice on the tick it is pressed (`collectNotices`), so the whole room
 * reads "X used Invisibility" — a name, no position, no entity id. That is deliberate and it is
 * what keeps this a threat somebody can play around rather than an unanswerable one: everybody
 * knows there is a ghost, nobody knows which corner it is in.
 */

import { TICK_RATE_HZ } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';

export const POWER_UP_INVISIBILITY = 'invisibility';

/**
 * How long a press hides you for, in seconds. **Thirty**, which is the report's own number and
 * the longest status in the registry by a factor of three.
 *
 * It can afford to be, because of what it is *not*: a freeze takes somebody else's agency and a
 * frost patch takes somebody else's speed, where this takes nothing from anybody. It is purely
 * defensive — the one thing it adds to the room is a player who cannot be stopped, and the price
 * is that everybody was told the moment it started.
 */
export const INVISIBILITY_DURATION_SECONDS = 30;

/** The same number the simulation counts in. `PlayerState.invisibleUntilTick` is a tick. */
export const INVISIBILITY_DURATION_TICKS = INVISIBILITY_DURATION_SECONDS * TICK_RATE_HZ;

/**
 * How long the slot lasts from the tick the orb is taken, in seconds — **also thirty**, and the
 * two being equal is the point rather than a coincidence.
 *
 * The window is the decision and the duration is the ability. Holding an unspent ghost for the
 * whole window costs you the ghost, exactly as holding an unspent rocket window costs you the
 * rockets; spending it on the first tick and spending it on the last both buy the same thirty
 * seconds. So the only thing a player is choosing is the moment, which is the entire skill in an
 * escape and is what a `charged` model — a budget that waits for ever — would have thrown away.
 */
export const INVISIBILITY_WINDOW_SECONDS = 30;

/** The window in the units the inventory holds it in. `InventorySlot.expiresAtTick` is a tick. */
export const INVISIBILITY_WINDOW_TICKS = INVISIBILITY_WINDOW_SECONDS * TICK_RATE_HZ;

/**
 * The gap between two presses of this slot, in ticks — **one second**, and it is the honest
 * declaration rather than the thing that bounds the ability.
 *
 * What bounds it is the one-window rule above: a press while the ghost is already running does
 * nothing, so there is no stream of firings for a cooldown to rate-limit. It is declared anyway,
 * for TELEPORT_FIRE_COOLDOWN_TICKS' reason — the model requires a number, a zero would say "as
 * fast as the wire allows" about the strongest defensive button in the game, and a second is what
 * somebody whose press did nothing waits before trying again.
 */
export const INVISIBILITY_FIRE_COOLDOWN_TICKS = TICK_RATE_HZ;

export const invisibilityPowerUp: PowerUp = {
  id: POWER_UP_INVISIBILITY,
  name: 'Invisibility',
  description: 'Vanish for 30 seconds: nothing can touch you, and you can still eat.',
  glyph: '👻',
  use: {
    model: 'timed',
    durationTicks: INVISIBILITY_WINDOW_TICKS,
    cooldownTicks: INVISIBILITY_FIRE_COOLDOWN_TICKS,
  },
  self: {
    effect: 'invisible',
    invisibleTicks: INVISIBILITY_DURATION_TICKS,
  },
};
